import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Switch,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import AddSchoolAdmin from "./AddSchoolAdmin";
import EditSchool from "./EditSchool";
import { Link } from "react-router-dom";
import {
  lockSchool,
  unlockSchool,
  isAuthenticated,
} from "../../../ApiService/ApiService";
import {
  School,
  LocationOn,
  Adjust,
  Paid,
  MoreHoriz,
  AdminPanelSettings,
  Add,
  Edit,
} from "@mui/icons-material";
import "../../../Styles/dashboard.css";
import CustomDialog from "../../../Components/CustomDialog";

const headCells = [
  {
    id: "name",
    label: "School Name",
    icon: <School sx={{ marginRight: 1 }} />,
  },
  {
    id: "address",
    label: "Address",
    icon: <LocationOn sx={{ marginRight: 1 }} />,
  },
  {
    id: "paymentplan",
    label: "PaymentPlan",
    icon: <Paid sx={{ marginRight: 1 }} />,
  },
  {
    id: "status",
    label: "Status",
    icon: <Adjust sx={{ marginRight: 1 }} />,
  },
  {
    id: "actions",
    label: "Actions",
    icon: <AdminPanelSettings sx={{ marginRight: 1 }} />,
  },
];

function SchoolTable({
  schools,
  fetchSchools,
  handleActionClick,
  setActionComponent,
}) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [paginatedData, setPaginatedData] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentSchoolId, setCurrentSchoolId] = useState(null);
  const [dialog, setDialog] = useState({
    visible: false,
    title: "",
    message: "",
    buttons: [],
  });

  useEffect(() => {
    setPaginatedData(schools?.data);
  }, [schools]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setCurrentSchoolId(null);
  };

  const handleDialogClose = () => {
    setDialog({ ...dialog, visible: false });
  };

  const handleChangeStatus = async (id, currentStatus) => {
    try {
      if (isAuthenticated()) {
        const response = currentStatus
          ? await lockSchool(id)
          : await unlockSchool(id);
        // console.log("RES ", response);
        if (response) {
          if (response?.data?.status === 200) {
            const updatedSchoolsData = paginatedData?.map((school) =>
              school?.id === id
                ? { ...school, isActive: !currentStatus }
                : school
            );
            setDialog({
              visible: true,
              title: "Success",
              message: "Status updated successfully",
              buttons: [{ name: "OK", onPress: handleDialogClose }],
            });
            setPaginatedData(updatedSchoolsData);
            fetchSchools();
          } else {
            const errorMessage =
              response?.data?.errorMessage || "Something went wrong";
            setDialog({
              visible: true,
              title: "Error",
              message: errorMessage,
              buttons: [{ name: "Close", onPress: handleDialogClose }],
            });
          }
        } else {
          setDialog({
            visible: true,
            title: "Error",
            message: "Invalid response from the server",
            buttons: [{ name: "Close", onPress: handleDialogClose }],
          });
        }
      } else {
        setDialog({
          visible: true,
          title: "Unauthorized action",
          message: "Please login and try again",
          buttons: [{ name: "Close", onPress: handleDialogClose }],
        });
      }
    } catch (error) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Something went wrong",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
    }
  };

  const actionsMenu = [
    {
      label: "Add Admin",
      icon: <Add fontSize="small" sx={{ marginRight: 1 }} />,
      onClick: () =>
        handleActionClick(
          <AddSchoolAdmin
            setActionComponent={setActionComponent}
            currentSchoolId={currentSchoolId}
          />
        ),
    },
    {
      label: "Edit",
      icon: <Edit fontSize="small" sx={{ marginRight: 1 }} />,
      onClick: () =>
        handleActionClick(
          <EditSchool
            setActionComponent={setActionComponent}
            currentSchoolId={currentSchoolId}
          />
        ),
    },
  ];

  const slicedData = paginatedData?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      <CustomDialog
        visible={dialog.visible}
        title={dialog.title}
        message={dialog.message}
        buttons={dialog.buttons}
        onClose={handleDialogClose}
      />
      <Box>
        <Paper>
          <TableContainer>
            <Table aria-labelledby="tableTitle">
              <TableHead>
                <TableRow>
                  {headCells.map((headCell) => (
                    <TableCell key={headCell.id} padding="normal">
                      <Box className="table-head">
                        {headCell.icon}
                        {headCell.label}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {slicedData?.length > 0 ? (
                  slicedData?.map((row) => (
                    <TableRow key={row?.id}>
                      <TableCell
                        component="th"
                        scope="row"
                        className="table-cell"
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Avatar
                            className="logo"
                            alt="School Logo"
                            src={row?.logo}
                            sx={{ height: 28, width: 28, marginRight: 1 }}
                          />
                          {row.name}
                        </Box>
                      </TableCell>
                      <TableCell className="table-cell">{`${row?.address?.street1}, ${row?.address?.city}, ${row?.address?.state} - ${row?.address?.pin}`}</TableCell>
                      <TableCell className="table-cell">
                        <Link className="link">{`${row?.plan?.duration} Days ${row?.plan?.name}`}</Link>
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={row?.isActive}
                          color={row?.isActive ? "success" : "default"}
                          onClick={() =>
                            handleChangeStatus(row?.id, row?.isActive)
                          }
                          inputProps={{ "aria-label": "controlled" }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={(event) => {
                            setAnchorEl(event.currentTarget);
                            setCurrentSchoolId(row.id);
                          }}
                        >
                          <MoreHoriz />
                        </IconButton>
                        <Menu
                          id="actions-menu"
                          anchorEl={anchorEl}
                          open={Boolean(anchorEl && currentSchoolId === row.id)}
                          onClose={handleClose}
                        >
                          {actionsMenu.map((action, index) => (
                            <MenuItem key={index} onClick={action.onClick}>
                              {action.icon}
                              {action.label}
                            </MenuItem>
                          ))}
                        </Menu>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={2}>
                      <Typography variant="body2" color="textSecondary">
                        No data available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {slicedData?.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[10]}
              component="div"
              count={paginatedData?.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Paper>
      </Box>
    </>
  );
}

SchoolTable.propTypes = {
  schools: PropTypes.object.isRequired,
  fetchSchools: PropTypes.func.isRequired,
  handleActionClick: PropTypes.func.isRequired,
};

export default SchoolTable;
